import { computed, inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { DaisiMenuItem } from '@models/menu.model';
import { MenuItem } from '@ndwnu/design-system';
import { NavigationEnd, Router } from '@angular/router';
import { IncidentReportModalComponent } from '@components/incident-report-modal/incident-report-modal.component';
import { CustomModalService } from '@services/custom-modal/custom-modal.service';
import { AuthService } from '@services/auth/auth.service';
import { AuthRoles } from '@models/AuthRoles';
import {
  overviewPath,
  roadworksPath,
  settingsPath,
  statisticsPath,
  trafficPath,
  userPath,
} from '@daisi/app.paths';
import { ReleaseNoteService } from '@services/release-notes/release-note.service';
import { filter } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  private readonly topMenuInitialized = signal(false);
  readonly topMenuItems: Signal<MenuItem[]> = computed(() => {
    this.currentUrl();
    if (!this.topMenuInitialized()) {
      return [];
    }
    return this.filterMenuItems([
      {
        icon: 'car_crash',
        label: 'Incidenten',
        roles: [AuthRoles.INCIDENTS],
        routerLink: overviewPath,
      },
      {
        icon: 'map',
        label: 'Verkeersbeeld',
        roles: [AuthRoles.TRAFFIC],
        routerLink: trafficPath,
      },
      {
        icon: 'construction',
        label: 'Wegwerkzaamheden',
        roles: [AuthRoles.ROADWORKS],
        routerLink: roadworksPath,
      },
      {
        icon: 'bar_chart',
        label: 'Statistieken',
        roles: [AuthRoles.DASHBOARD],
        routerLink: statisticsPath,
      },
    ]);
  });
  private readonly router: Router = inject(Router);
  private readonly authService: AuthService = inject(AuthService);
  private readonly customModelService: CustomModalService = inject(CustomModalService);
  private readonly releaseNoteService: ReleaseNoteService = inject(ReleaseNoteService);
  private readonly currentUrl: WritableSignal<string> = signal(this.router.url);
  private readonly BOTTOM_MENU_ITEMS: Signal<DaisiMenuItem[]> = computed<DaisiMenuItem[]>(() => [
    {
      label: 'Beheer',
      icon: 'admin_panel_settings',
      roles: [AuthRoles.ADMIN, AuthRoles.USER_ADMIN],
      subItems: [
        {
          label: 'Gebruikersbeheer',
          routerLink: userPath,
          roles: [AuthRoles.USER_ADMIN],
        },
        {
          label: 'Infoberichten',
          routerLink: '/infoberichten',
          roles: [AuthRoles.ADMIN],
        },
        {
          label: 'Release notes',
          routerLink: '/release-notes',
          roles: [AuthRoles.ADMIN],
        },
      ],
    },
    {
      icon: 'layers',
      label: 'NDW Viewer',
      externalLink: 'https://ndwviewer.ndw.nu/',
    },
    {
      icon: 'download',
      label: 'Exports',
      roles: [AuthRoles.INCIDENTS],
      subItems: [
        {
          label: 'Incidenten historie',
          callback: (): IncidentReportModalComponent | undefined =>
            this.customModelService.createAndOpen(IncidentReportModalComponent),
          roles: [AuthRoles.INCIDENTS],
        },
      ],
    },
    {
      icon: 'notifications',
      label: 'Updates',
      routerLink: '/updates',
      notifications: this.releaseNoteService.unreadSmall().length,
    },
    {
      icon: 'settings',
      label: 'Instellingen',
      routerLink: `/${settingsPath}`,
    },
    {
      icon: 'help',
      label: 'Hulp',
      subItems: [
        {
          label: 'Handleiding',
          externalLink: 'https://docs.ndw.nu/handleidingen/Daisi/',
        },
        {
          label: 'Veelgestelde vragen',
          externalLink: 'https://docs.ndw.nu/handleidingen/Daisi/veelgestelde-vragen/',
        },
        {
          label: 'Contact',
          externalLink: 'https://docs.ndw.nu/handleidingen/Daisi/contact/',
        },
      ],
    },
    {
      icon: 'account_circle',
      label: this.authService.currentUser()?.name || 'Gebruiker',
      subItems: [
        {
          label: 'Uitloggen',
          callback: (): void => this.authService.logout(),
        },
      ],
    },
  ]);
  readonly bottomMenuItems: Signal<MenuItem[]> = computed((): MenuItem[] =>
    this.filterMenuItems(this.BOTTOM_MENU_ITEMS()),
  );

  constructor() {
    this.getNrUnreadReleaseNotesBadge();
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.currentUrl.set(event.urlAfterRedirects));
  }

  init() {
    this.topMenuInitialized.set(true);
  }

  canActivateMenuItem(menuRoles: AuthRoles[]): boolean {
    if (!menuRoles || !menuRoles.length) {
      return true;
    } else {
      return this.authService.hasRoles(menuRoles);
    }
  }

  getNrUnreadReleaseNotesBadge(): void {
    const userId: string | undefined = this.authService.getUser()?.userId;
    if (!userId) {
      return;
    }
  }

  private filterMenuItems(menu: DaisiMenuItem[]): MenuItem[] {
    if (menu === undefined) {
      return [];
    }
    const url = this.currentUrl();
    const result: MenuItem[] = [];
    menu.forEach((item: DaisiMenuItem): void => {
      // @ts-ignore
      if (this.canActivateMenuItem(item.roles) && item.hide !== true) {
        const children = item.subItems ? this.filterMenuItems(item.subItems ?? []) : undefined;
        const isChildActive = children?.some((child) => child.active) ?? false;
        result.push({
          label: item.label ?? '',
          icon: item.icon ?? '',
          callback: this.generateCallback(item),
          children,
          notifications: item.notifications,
          active: isChildActive || this.isMenuItemActive(item, url),
        });
      }
    });
    return result;
  }

  private isMenuItemActive(item: DaisiMenuItem, url: string): boolean {
    if (!item.routerLink) {
      return false;
    }
    const link = item.routerLink.startsWith('/') ? item.routerLink : `/${item.routerLink}`;
    return url === link || url.startsWith(`${link}/`);
  }

  private generateCallback(menuItem: DaisiMenuItem): () => void {
    if (menuItem.routerLink) {
      const link: string = menuItem.routerLink;
      return (): Promise<boolean> => this.router.navigateByUrl(link);
    } else if (menuItem.externalLink) {
      return (): void | undefined => window.open(menuItem.externalLink, '_blank')?.focus();
    } else if (menuItem.callback) {
      return menuItem.callback;
    }
    return () => {};
  }
}
